import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import CloseIcon from "./icons";

export default function AuthDialog({ open, initialMode = "signin", onClose }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [username, setUsername] = useState("");
  const { signIn, signUp, signInWithGoogle } = useAuth();

  useEffect(() => {
    if (!open) {
      setEmail("");
      setPassword("");
      setUsername("");
      setError(null);
      setInfo(null);
      setMode(initialMode);
    }
  }, [open, initialMode]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      if (mode === "signin") {
        const { error } = await signIn(email, password);
        if (error) throw error;
        onClose?.();
      } else {
        // Basic client-side validation matching the DB constraint
        if (!/^[a-zA-Z0-9_-]{3,20}$/.test(username)) {
          throw new Error(
            "Username must be 3-20 chars, letters/numbers/underscore/hyphen.",
          );
        }
        const { data, error } = await signUp(email, password, username);
        if (error) throw error;
        if (data.session) {
          onClose?.();
        } else {
          setInfo(
            "Check your email to confirm your account. Check your spam folder if you don't see it.",
          );
        }
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    setError(null);
    const { error } = await signInWithGoogle();
    if (error) setError(error.message);
    // on success the browser redirects to Google — nothing else to do here
  };

  function GoogleIcon() {
    return (
      <svg viewBox="0 0 48 48" className="w-4 h-4">
        <path
          fill="#FFC107"
          d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
        />
        <path
          fill="#FF3D00"
          d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
        />
        <path
          fill="#4CAF50"
          d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
        />
        <path
          fill="#1976D2"
          d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
        />
      </svg>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm grid place-items-center p-6"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-sm
                   p-6 animate-scale-in shadow-2xl shadow-black/50"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-zinc-100">
            {mode === "signin" ? "Sign in" : "Create account"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 grid place-items-center rounded-full text-zinc-500
                       hover:text-zinc-100 hover:bg-zinc-900 transition"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        <button
          type="button"
          onClick={handleGoogle}
          className="w-full flex items-center justify-center gap-2.5 py-2.5 rounded-full
                     bg-white text-zinc-900 font-semibold text-sm hover:bg-zinc-100 transition"
        >
          <GoogleIcon />
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-zinc-800" />
          <span className="text-[10px] uppercase tracking-widest text-zinc-600">
            or
          </span>
          <div className="flex-1 h-px bg-zinc-800" />
        </div>

        <div className="space-y-3">
          {mode === "signup" && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                Username
              </span>
              <input
                type="text"
                autoComplete="username"
                required
                minLength={3}
                maxLength={20}
                pattern="[a-zA-Z0-9_\-]+"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-lg
                 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600
                 focus:outline-none focus:border-zinc-700 transition"
                placeholder="username"
              />
            </label>
          )}

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">
              Email
            </span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-lg
                         px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600
                         focus:outline-none focus:border-zinc-700 transition"
              placeholder="you@example.com"
            />
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">
              Password
            </span>
            <input
              type="password"
              autoComplete={
                mode === "signin" ? "current-password" : "new-password"
              }
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-lg
                         px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600
                         focus:outline-none focus:border-zinc-700 transition"
              placeholder="••••••••"
            />
          </label>
        </div>

        {error && (
          <div className="mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}
        {info && (
          <div className="mt-3 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
            {info}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="mt-5 w-full py-2.5 rounded-full bg-emerald-500 text-zinc-950
                     font-semibold text-sm hover:bg-emerald-400 transition
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? "…" : mode === "signin" ? "Sign in" : "Create account"}
        </button>

        <div className="mt-4 text-center text-xs text-zinc-500">
          {mode === "signin" ? (
            <>
              No account?{" "}
              <button
                type="button"
                onClick={() => {
                  setMode("signup");
                  setError(null);
                  setInfo(null);
                }}
                className="text-emerald-400 font-semibold hover:text-emerald-300 transition"
              >
                Create one
              </button>
            </>
          ) : (
            <>
              Already have one?{" "}
              <button
                type="button"
                onClick={() => {
                  setMode("signin");
                  setError(null);
                  setInfo(null);
                }}
                className="text-emerald-400 font-semibold hover:text-emerald-300 transition"
              >
                Sign in
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
